import { useState } from "react";
import { Link } from "react-router-dom";
import { Camera, QrCode, Images, ClipboardPaste, AlertTriangle, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { StatusChip, usePoll, displayName } from "@/components/common";
import ActiveEventPicker from "@/components/ActiveEventPicker";
import DigestCard from "@/components/DigestCard";

const ACTIONS = [
  ["/capture?mode=card", "Scan business card", Camera, "home-scan-card-btn"],
  ["/capture?mode=qr", "Scan QR", QrCode, "home-scan-qr-btn"],
  ["/batch", "Upload photos", Images, "home-upload-photos-btn"],
  ["/capture?mode=paste", "Paste details", ClipboardPaste, "home-paste-details-btn"],
];

function Stat({ label, value, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 hover:border-teal-500">
      <p className="text-2xl font-bold font-display">{value ?? "–"}</p>
      <p className="text-xs font-medium text-slate-500">{label}</p>
    </Link>
  );
}

function Row({ c }) {
  return (
    <Link to={`/contacts/${c.id}`} data-testid={`home-contact-${c.id}`} className="flex items-center justify-between gap-3 border-b border-slate-100 py-3 last:border-0">
      <div className="min-w-0">
        <p className="truncate font-semibold">{displayName(c)}</p>
        <p className="truncate text-sm text-slate-500">{c.company && c.full_name ? c.company : (c.review_reasons || []).join(" · ")}</p>
      </div>
      <div className="flex items-center gap-2"><StatusChip status={c.status} /><ChevronRight className="h-4 w-4 text-slate-300" /></div>
    </Link>
  );
}

export default function Home() {
  const [d, setD] = useState(null);
  usePoll(() => api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}), 8000);
  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between cd-fade">
        <h1 className="text-2xl md:text-3xl font-bold">Capture a contact</h1>
        <ActiveEventPicker compact />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {ACTIONS.map(([to, label, Icon, tid], i) => (
          <Link key={to} to={to} data-testid={tid} style={{ animationDelay: `${i * 60}ms` }}
            className="cd-fade flex h-32 flex-col items-start justify-between rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-teal-500 hover:shadow-md active:scale-[0.98]">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50 text-teal-700"><Icon className="h-6 w-6" /></span>
            <span className="font-display text-[15px] font-bold leading-tight">{label}</span>
          </Link>
        ))}
      </div>
      <DigestCard />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Contacts" value={d?.total_contacts} to="/contacts" testid="stat-total-contacts" />
        <Stat label="Need review" value={d?.needs_review} to="/contacts?status=needs_review" testid="stat-needs-review" />
        <Stat label="Follow-ups due" value={d?.followups_due} to="/followups" testid="stat-followups-due" />
        <Stat label="Drafts" value={d?.drafts} to="/campaigns" testid="stat-drafts" />
      </div>
      {(d?.processing > 0 || d?.failed_captures > 0 || d?.uncertain_sends > 0) && (
        <div className="flex flex-wrap gap-2 text-sm" data-testid="home-alerts">
          {d.processing > 0 && <span className="rounded-full bg-violet-50 px-3 py-1 font-medium text-violet-700">{d.processing} capture(s) extracting…</span>}
          {d.failed_captures > 0 && <Link to="/batch" className="rounded-full bg-rose-50 px-3 py-1 font-medium text-rose-700">{d.failed_captures} failed extraction(s) — retry or enter manually</Link>}
          {d.uncertain_sends > 0 && <Link to="/campaigns" className="rounded-full bg-amber-50 px-3 py-1 font-medium text-amber-800">{d.uncertain_sends} email(s) with uncertain outcome</Link>}
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="cd-card" data-testid="home-needs-review">
          <h2 className="mb-2 flex items-center gap-2 text-lg font-bold"><AlertTriangle className="h-5 w-5 text-amber-500" />Needs review</h2>
          {d?.review?.length ? d.review.map((c) => <Row key={c.id} c={c} />) : <p className="py-4 text-sm text-slate-500">Nothing waiting for review.</p>}
        </section>
        <section className="cd-card" data-testid="home-recent-contacts">
          <h2 className="mb-2 text-lg font-bold">Recent contacts</h2>
          {d?.recent?.length ? d.recent.map((c) => <Row key={c.id} c={c} />) : <p className="py-4 text-sm text-slate-500">No contacts yet. Scan your first business card above.</p>}
        </section>
      </div>
    </div>
  );
}
